import { RouteConfig } from 'vue-router';
import { ModelType } from '@llws/platform-shared-types/types/db/auth';
import app from '@/main';
import { CurrentUser, IRouteConfigAuthMeta, IRouteMetaPermissionConfig } from '@/shared';
import store from '@/store';

const auth: IRouteConfigAuthMeta = {
  required: true,
  isDynamicPermitted: () => {
    const user: CurrentUser = store.getters['users/current'];
    return ['tour_admins', 'tour_agents'].some((p) => user.hasAtLeastOneModel(p));
  },
};

const tours: RouteConfig = {
  path: '/tours',
  name: 'Tours',
  component: () => import('@/views/tours/Index.vue'),
  meta: {
    auth,
    title: 'Tours',
    icon: 'calendar-day',
    noPagePadding: true,
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: vm?.$route.path ?? '',
        title: 'Tours List',
      },
    ],
  },
};

const toursSingleAdd: RouteConfig = {
  path: '/tours/new',
  name: 'Add Tour',
  component: () => import('@/views/tours/single/new/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['tour_admins', 'tour_agents'].map((tour) => ({
        key: tour,
        keyType: ModelType.building,
        accessType: 'create',
        checkAtLeastOneModel: true,
      })) as IRouteMetaPermissionConfig[],
      condition: 'or',
    },
    parent: tours,
    title: 'Add Tour',
    icon: 'calendar-day',
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    hideFromNav: true,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: false,
    },
    hideIframeBackButton: true,
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Add Tour',
      },
    ],
  },
};

const toursSingleEdit: RouteConfig = {
  path: '/tours/edit/:bookingResponseId',
  name: 'Edit Tour',
  component: () => import('@/views/tours/single/Index.vue'),
  meta: {
    auth,
    parent: tours,
    title: 'Edit Tour',
    icon: 'calendar-day',
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    hideFromNav: true,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: '/tours',
        title: 'Tours List',
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: 'Details',
      },
    ],
  },
};

const calendar: RouteConfig = {
  path: '/tours/calendar',
  name: 'Calendar',
  component: () => import('@/views/tours/calendar/Index.vue'),
  meta: {
    auth,
    title: 'Calendar',
    icon: 'calendar-day',
    noPagePadding: true,
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const toursSettings: RouteConfig = {
  path: '/tours/settings',
  name: 'Settings',
  component: () => import('@/views/tours/Index.vue'),
  redirect: () => {
    try {
      return app()?.$rsGlobals.$hasToursRestricted()
        ? `/tours/settings/agent-settings/${app()?.$rsGlobals.$currentUser.userId}/agent`
        : '/tours/settings/tour-general';
    } catch (e) {
      console.warn(e);
      return '/tours/settings/tour-general';
    }
  },
  meta: {
    auth,
    title: 'Settings',
    pageTitle: 'Settings',
    icon: 'calendar-day',
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
  },
};

const tourGeneral: RouteConfig = {
  path: '/tours/settings/tour-general',
  name: 'General Settings',
  component: () => import(/* webpackChunkName: "group-syndication" */ '@/views/tours/settings/tour-general/Index.vue'),
  meta: {
    auth,
    title: 'General Settings',
    pageTitle: 'General Settings',
    icon: 'calendar-day',
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    parent: toursSettings,
    footerBar: {
      enabled: true,
      hasTranslations: false,
      hasTranslationsBtn: () => !(app()?.$rsGlobals.$siteSourceClientLanguages?.length),
      languageOptions: () => app()?.$rsGlobals.$clientAndSiteLanguageOptions ?? [],
      hasSave: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: '/tours/settings',
        title: 'Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};
const tourSuperAdminGeneral: RouteConfig = {
  path: '/tours/settings/super-admin/general',
  name: 'Super Admin General Settings',
  component: () => import(/* webpackChunkName: "group-syndication" */ '@/views/tours/settings/super-admin-settings/Index.vue'),
  meta: {
    auth,
    title: 'General Settings',
    pageTitle: 'General Settings',
    icon: 'calendar-day',
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    parent: toursSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: '/tours/settings',
        title: 'Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const tourGoogleAdsSetting: RouteConfig = {
  path: '/tours/settings/super-admin/ad-event-tracking',
  name: 'Super Admin Ads',
  component: () => import(/* webpackChunkName: "group-syndication" */ '@/views/tours/settings/super-admin-settings/AdTrackingSettings.vue'),
  meta: {
    auth,
    title: 'General Settings',
    pageTitle: 'General Settings',
    icon: 'calendar-day',
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    parent: toursSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: '/tours/settings',
        title: 'Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const tourForms: RouteConfig = {
  path: '/tours/settings/tour-forms',
  name: 'Form Settings',
  component: () => import('@/views/tours/settings/tour-forms/Index.vue'),
  meta: {
    auth,
    title: 'Form Settings',
    pageTitle: 'Form Settings',
    icon: 'calendar-day',
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    parent: toursSettings,
    footerBar: {
      enabled: true,
      hasTranslations: true,
      languageOptions: () => app()?.$rsGlobals.$clientAndSiteLanguageOptions ?? [],
      hasSave: true,
      hasTranslationsBtn: () => true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: '/tours/settings',
        title: 'Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const tourProperties: RouteConfig = {
  path: '/tours/settings/tour-properties',
  name: 'Property Settings',
  component: () => import('@/views/tours/settings/tour-properties/Index.vue'),
  meta: {
    auth,
    title: 'Property Settings',
    pageTitle: 'Property Settings',
    icon: 'calendar-day',
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    parent: toursSettings,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: '/tours/settings',
        title: 'Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const tourPropertySettings: RouteConfig = {
  path: '/tours/settings/tour-properties/:propertyId',
  name: 'Property Settings Single',
  component: () => import('@/views/tours/settings/tour-properties/single/Index.vue'),
  meta: {
    auth,
    title: 'Property Settings',
    pageTitle: 'Edi',
    icon: 'calendar-day',
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    noPagePadding: true,
    hideFromNav: true,
    backButtonTarget: () => ({ isCustom: false, target: tourProperties }),
    footerBar: {
      enabled: true,
      hasSave: true,
      hasBottomText: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: '/tours/settings',
        title: 'Settings',
      },
      {
        to: '/tours/settings/tour-properties',
        title: 'Property Settings',
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: 'Details',
      },
    ],
  },
};

const tourDefaultPropertySettings: RouteConfig = {
  path: '/tours/settings/default-property-settings',
  name: 'Default Property Settings',
  component: () => import('@/views/tours/settings/tour-properties/default-settings/Index.vue'),
  meta: {
    auth,
    title: 'Default Property Settings',
    pageTitle: 'Default Property Settings',
    icon: 'calendar-day',
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    noPagePadding: true,
    hideFromNav: true,
    backButtonTarget: () => ({ isCustom: false, target: tourProperties }),
    footerBar: {
      enabled: true,
      hasSave: true,
      hasBottomText: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: '/tours/settings',
        title: 'Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const agentSettings: RouteConfig = {
  path: '/tours/settings/agent-settings',
  name: 'Agent Settings',
  component: () => import('@/views/tours/settings/agent-settings/Index.vue'),
  children: [],
  meta: {
    auth,
    title: 'Agent Settings',
    pageTitle: 'Tours Agent Settings',
    icon: 'calendar-day',
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    parent: toursSettings,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: '/tours/settings',
        title: 'Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const adminAgentSettingsSingle: RouteConfig = {
  path: '/tours/settings/agent-settings/:userId/admin/',
  name: 'AdminAgentSettingsSingle',
  component: () => import('@/views/tours/settings/agent-settings/single/Index.vue'),
  meta: {
    auth,
    active: false,
    title: 'Admin Settings',
    icon: 'calendar-day',
    noPagePadding: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    hideFromNav: true,
    parent: agentSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasBottomText: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: '/tours/settings',
        title: 'Settings',
      },
      {
        to: '/tours/settings/agent-settings',
        title: 'Agent Settings',
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: 'Details',
      },
    ],
  },
};

const agentSettingsSingle: RouteConfig = {
  path: '/tours/settings/agent-settings/:userId/agent',
  name: 'AgentSettingsSingle',
  component: () => import('@/views/tours/settings/agent-settings/single/Index.vue'),
  meta: {
    auth,
    active: false,
    title: 'Settings',
    icon: 'calendar-day',
    noPagePadding: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    hideFromNav: true,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasBottomText: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/tours',
        title: 'Tours',
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: 'Settings',
      },
    ],
  },
};

const nylasAgentSettingsRedirect: RouteConfig = {
  path: '/tours/nylas/settings/agent-settings',
  name: 'Agent Settings Nylas',
  component: () => import('@/views/tours/settings/agent-settings/single/Index.vue'),
  children: [],
  meta: {
    auth,
    title: 'Agent Settings',
    pageTitle: 'Tours Agent Settings',
    icon: 'calendar-day',
    active: false,
    infoUrl: 'y',
    reportingUrl: '/reporting/tours',
    parent: toursSettings,
    noPagePadding: true,
    hideFromNav: true,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasBottomText: true,
    },
  },
};

export default [
  tours,
  calendar,
  toursSingleAdd,
  toursSingleEdit,
  toursSettings,
  tourGeneral,
  tourSuperAdminGeneral,
  tourForms,
  tourProperties,
  tourPropertySettings,
  agentSettings,
  adminAgentSettingsSingle,
  agentSettingsSingle,
  tourDefaultPropertySettings,
  nylasAgentSettingsRedirect,
  tourGoogleAdsSetting,
];
